import { FLAG_ENABLE_DATA_LAKE_MONITOR } from '@mst-fe/shared/constants/zoe-user-flags';
import React from 'react';
import { Navigate, Route } from 'react-router';

// FIXME Importing these here means we are including these MIDAS components in Commercial bundle.
// Same goes for using DataQueryWorkflowPageWrapper in commercial wb.
// UDT creation is too entangled with MIDAS workflows, to avoid pulling MIDAS code in commercial requires a big refactoring there.
import { QueryContextProviderWrapperRoute } from '../midas/context/QueryContextProviderWrapper';
import { SettingsContextProviderWrapperRoute } from '../midas/context/SettingsContextProviderWrapper';
import { UserInfoModel } from '../pages/user-info/models/user-info';
import {
  APP_MONITOR_KEY,
  BELLPORT_MESSAGE_MAPPING_KEY,
  DATA_LAKE_MONITOR_KEY,
  DOCUMENTATION_CHAT,
  FEEDS_MONITOR_KEY,
  INSTANCES_ITEM_KEY,
  RELEASE_HISTORY_KEY,
  SLA_MONITOR_KEY,
  STATUS_DASHBOARD_KEY,
} from '../side-nav/sidenav-constants';
import routes, { MyRoute } from '../utils/routes';
import { suspendedLazyComponent } from './views/SuspendedLazyComponent';

const AuthorizeDevice = suspendedLazyComponent(React.lazy(() => import('../pages/device/views/AuthorizeDevice')));
const Dashboards = suspendedLazyComponent(React.lazy(() => import('../pages/dashboards/views/Dashboards')));
const WelcomeScreen = suspendedLazyComponent(React.lazy(() => import('../midas/pages/welcome/WelcomeScreen')));
const DashboardDetailScreen = suspendedLazyComponent(React.lazy(() => import('../pages/dashboards/views/DashboardDetailScreen')));
const PublishedManifestsTabs = suspendedLazyComponent(React.lazy(() => import('../pages/services/views/PublishedManifestsTabs')));
const ServiceDetails = suspendedLazyComponent(React.lazy(() => import('../pages/services/views/ServiceDetails')));
const JobDetails = suspendedLazyComponent(React.lazy(() => import('../pages/jobs/views/JobDetails')));
const WorkbenchResources = suspendedLazyComponent(React.lazy(() => import('../pages/resources/views/WorkbenchResources')));
const InstanceTabs = suspendedLazyComponent(React.lazy(() => import('../pages/instances/views/InstanceTabs')));
const FileExchange = suspendedLazyComponent(React.lazy(() => import('../pages/file-exchange/views/FileExchange')));
const DpActivityPage = suspendedLazyComponent(React.lazy(() => import('../midas/pages/dpActivity/DpActivityPage')));
const DataQueryWorkflowPageWrapper = suspendedLazyComponent(React.lazy(async () => import('../midas/pages/DataQueryWorkflowPageWrapper')));
const Usage = suspendedLazyComponent(React.lazy(() => import('../pages/usage/views/Usage')));
const MarkdownDocumentation = suspendedLazyComponent(React.lazy(() => import('../pages/documentation/views/MarkdownDocumentation')));
const DocumentationScreen = suspendedLazyComponent(React.lazy(() => import('../pages/documentation/views/DocumentationScreen')));
const ReleaseHistoryScreen = suspendedLazyComponent(React.lazy(() => import('../pages/release-history/views/ReleaseHistoryScreen')));
const MappingsScreen = suspendedLazyComponent(React.lazy(() => import('../pages/bellport-message-mapping/views/MappingsScreen')));
const NotificationsScreen = suspendedLazyComponent(React.lazy(() => import('../pages/notifications/views/NotificationsScreen')));
const RawFeedData = suspendedLazyComponent(React.lazy(() => import('../pages/raw-feed-data/views/RawFeedData')));
const FeedsMonitor = suspendedLazyComponent(React.lazy(() => import('../pages/monitoring/views/FeedsMonitor')));
const StatusDashboard = suspendedLazyComponent(React.lazy(() => import('../pages/monitoring/views/StatusDashboard')));
const CancelUserResourcesDeletion = suspendedLazyComponent(React.lazy(() => import('../pages/users/views/CancelUserResourcesDeletion')));
const AppMonitorPage = suspendedLazyComponent(React.lazy(() => import('../pages/app-monitor/views/AppMonitorPage')));
const SpringboardBrowserView = suspendedLazyComponent(
  React.lazy(() => import('../pages/springboard-browser/views/SpringboardBrowserView'))
);
const SupersetPage = suspendedLazyComponent(React.lazy(() => import('../pages/superset/views/SupersetPage')));
const GlobalSearchPage = suspendedLazyComponent(React.lazy(() => import('../pages/global-search/views/GlobalSearch')));
const DataLakeMonitorPage = suspendedLazyComponent(React.lazy(() => import('../pages/monitoring/views/DataLakeMonitor')));
const SlaMonitorDashboard = suspendedLazyComponent(React.lazy(() => import('../pages/monitoring/views/SlaMonitorDashboard')));
const MyData = suspendedLazyComponent(React.lazy(() => import('../pages/my-data/MyData')));
const AiChatPage = suspendedLazyComponent(React.lazy(() => import('../pages/ai/views/AiChatPage')));

export function createAppRoutes(userInfoModel: UserInfoModel) {
  const isMidasPeer = window.CONFIG.isMidasPeer;
  const { enableBedrockDocumentation } = window.CONFIG.featureFlags;

  const isWbOrLsegUser = userInfoModel.isLsegUser || userInfoModel.workbenchAllowed;
  const enableDataLakeMonitor = userInfoModel.featureFlags.has(FLAG_ENABLE_DATA_LAKE_MONITOR);

  return (
    <>
      {/* User actions routes */}
      <Route path={`${routes.AuthorizeDevice}/:deviceCode`} element={<AuthorizeDevice />} />
      <Route path={`${routes.CancelUserResourcesDeletion}/:userId`} element={<CancelUserResourcesDeletion />} />

      {/* Global search route */}
      <Route path={`${routes.GlobalSearchResult}`} element={<GlobalSearchPage />} />

      {/* Index route */}
      {isMidasPeer || !userInfoModel.workbenchAllowed ? (
        <Route path="/" element={<WelcomeScreen />} />
      ) : (
        <Route path="/" element={<Dashboards />} />
      )}

      {/* Dashboards routes */}
      <Route
        path={`${routes.Dashboard}/:dashboardId`}
        element={userInfoModel.workbenchAllowed ? <DashboardDetailScreen /> : <Navigate to="/" />}
      />
      <Route path={routes.Dashboard} element={userInfoModel.workbenchAllowed ? <Dashboards /> : <Navigate to="/" />} />

      {/* Applications & Jobs & Resources routes */}
      <Route path={routes.PublishedManifests} element={userInfoModel.workbenchAllowed ? <PublishedManifestsTabs /> : <Navigate to="/" />} />
      <Route path={`${routes.Applications}/:applicationId`} element={<ServiceDetails />} />
      <Route path={`${routes.Jobs}/:jobId`} element={<JobDetails />} />
      <Route path={routes.Resources} element={userInfoModel.workbenchAllowed ? <WorkbenchResources /> : <Navigate to="/" />} />

      {/* Instances routes */}
      <Route path={routes.Instances} element={userInfoModel.workbenchAllowed ? <InstanceTabs /> : <Navigate to="/" />} />

      {/* My backward compatibility */}
      <Route path={MyRoute.Instances} element={userInfoModel.workbenchAllowed ? <InstanceTabs /> : <Navigate to="/" />} />
      {isMidasPeer && (
        // alias for old docs links
        <Route path={routes.Peer} element={<Navigate to={INSTANCES_ITEM_KEY} />} />
      )}
      <Route path={`${routes.Instances}/:instanceId`} element={null} />

      {/* File explorer routes */}
      <Route path={`${routes.FileExplorer}`} element={<FileExchange />} />
      <Route path={`${routes.FileExplorer}/*`} element={<FileExchange />} />
      <Route path={`${MyRoute.FileExplorer}/:providerName?/:folderId?`} element={<FileExchange />} />

      {/* Workflows & Data routes */}
      {!isMidasPeer && (isMidasPeer || isWbOrLsegUser) && <Route path={routes.DpState} element={<MyData />} />}
      <Route element={<SettingsContextProviderWrapperRoute />}>
        <Route element={<QueryContextProviderWrapperRoute />}>
          {(isMidasPeer || isWbOrLsegUser) && (
            <Route path={routes.Workflow} element={<DataQueryWorkflowPageWrapper isWorkflowsPage={true} />} />
          )}
          {(isMidasPeer || isWbOrLsegUser) && (
            <Route path={`${routes.Workflow}/:workflowId`} element={<DataQueryWorkflowPageWrapper isWorkflowsPage={true} />} />
          )}

          {isMidasPeer && (
            <>
              <Route path={routes.DpState} element={<DpActivityPage />} />
              <Route path={routes.Data} element={<DataQueryWorkflowPageWrapper isWorkflowsPage={false} />} />
              <Route path={`${routes.Data}/:data`} element={<DataQueryWorkflowPageWrapper isWorkflowsPage={false} />} />
            </>
          )}
        </Route>
      </Route>

      {/* Usage routes */}
      <Route path={`${routes.Usage}/*`} element={<Usage />} />

      {/* Raw Feed Data routes */}
      {(isMidasPeer || isWbOrLsegUser) && <Route path={`${routes.RawFeedData}`} element={<RawFeedData />} />}

      {/* Documentation routes */}
      <Route path={`${routes.Documentation}/midas-landing-page/*`} element={<MarkdownDocumentation docType="midas-landing-page" />}></Route>
      <Route path={`${routes.Documentation}/midas-user-guide/*`} element={<MarkdownDocumentation docType="midas-user-guide" />}></Route>
      <Route path={`${routes.Documentation}/thw-user-guide/*`} element={<MarkdownDocumentation docType="thw-user-guide" />}></Route>
      <Route path={`${routes.Documentation}/:documentationId`} element={<DocumentationScreen />} />
      <Route path={`${routes.Documentation}/:documentationId/*`} element={<DocumentationScreen />} />
      <Route path={`${routes.Documentation}/${RELEASE_HISTORY_KEY}`} element={<ReleaseHistoryScreen />} />
      <Route path={`${routes.Documentation}/${BELLPORT_MESSAGE_MAPPING_KEY}/:exchangeId/:feedId/:query`} element={<MappingsScreen />} />
      <Route path={`${routes.Documentation}/${BELLPORT_MESSAGE_MAPPING_KEY}`} element={<MappingsScreen />} />

      {enableBedrockDocumentation && <Route path={`${routes.Documentation}/${DOCUMENTATION_CHAT}`} element={<AiChatPage />} />}

      {/* Notification routes */}
      <Route path={routes.Notifications} element={<NotificationsScreen />} />

      {/* Monitoring routes */}
      {enableDataLakeMonitor && (isMidasPeer || isWbOrLsegUser) && (
        <Route path={`${routes.Monitoring}/${DATA_LAKE_MONITOR_KEY}`} element={<DataLakeMonitorPage />} />
      )}
      {isMidasPeer && <Route path={`${routes.Monitoring}/${SLA_MONITOR_KEY}`} element={<SlaMonitorDashboard />} />}
      {(isMidasPeer || isWbOrLsegUser) && <Route path={`${routes.Monitoring}/${FEEDS_MONITOR_KEY}`} element={<FeedsMonitor />} />}
      {isMidasPeer && <Route path={`${routes.Monitoring}/${STATUS_DASHBOARD_KEY}`} element={<StatusDashboard />} />}
      <Route path={`${routes.Monitoring}/${APP_MONITOR_KEY}`} element={<AppMonitorPage />} />

      {/* Springboard browser route */}
      <Route path={routes.SpringboardBrowser} element={<SpringboardBrowserView />} />

      {/* Superset route */}
      <Route path={routes.SupersetVisualisations} element={<SupersetPage />} />
    </>
  );
}
